"use client";
import useTranslate from "@app/_hooks/useTranslate";

import { cn } from "@/lib/utils";
import { useSearchStore } from "@/stores/searchStore";
import SearchIcon from "@app/_icons/SearchIcon/SearchIcon";
import MobileMenu from "@components/MobileMenu/MobileMenu";
import { useHeaderContext } from "@context/headerContext";
import type { ReactNode } from "react";
import { useMemo } from "react";
import Search from "../search";
import Actions from "./Actions";
import Menu from "./Menu";

export default function Content({ children }: { children: ReactNode }) {
  const t = useTranslate();
  const { scrollDirection, hasScrolled } = useHeaderContext();
  const mobileSearchOpen = useSearchStore((state) => state.mobileSearchOpen);
  const setMobileSearchOpen = useSearchStore(
    (state) => state.setMobileSearchOpen,
  );
  const hideSearch = useMemo(
    () => hasScrolled && scrollDirection === "down" && mobileSearchOpen,
    [hasScrolled, scrollDirection, mobileSearchOpen],
  );

  return (
    <div className="lg:child:pointer-events-auto lg:pointer-events-none">
      <div className="relative z-10 bg-white">
        <div className="relative container grid h-12 w-full grid-cols-[4rem_auto_4rem] items-stretch justify-between bg-white px-3 lg:z-40 lg:h-14 lg:grid-cols-[1fr_auto_1fr]">
          <div className="flex items-center gap-1">
            <MobileMenu />
            <button
              type="button"
              className={cn(
                "p-1 transition-opacity duration-100 lg:hidden",
                !hideSearch && "pointer-events-none opacity-0",
              )}
              onClick={() => {
                setMobileSearchOpen(!mobileSearchOpen);
              }}
            >
              <SearchIcon className="size-4" />
              <span className="sr-only">{t("search")}</span>
            </button>
            <Search
              searchInputId="search-input-desktop"
              classNames={{
                root: "max-lg:hidden lg:max-w-[220px]",
                input: "border-gray-150 focus:border-gray-200",
              }}
            />
          </div>
          {children}
          <div className="flex items-center justify-end">
            <Actions />
          </div>
        </div>
      </div>
      <Menu />
      <Search
        searchInputId="search-input-mobile"
        classNames={{
          root: cn(
            "transition-transform duration-100 lg:hidden",
            hideSearch && "-translate-y-full",
          ),
          input:
            "h-10 pl-11 border-t border-b border-color-gray-150 border-x-0 border-gray-150 placeholder:text-sm",
          icon: "left-4",
        }}
      />
    </div>
  );
}
